import { usePostHog } from '@/composables/usePostHog';
import type { ActivityPayload } from '@/types/activity';

class ActivityBuilder {
    private payload: ActivityPayload = {};
    private errorCode?: string;
    private errorMessage?: string;

    constructor(
        private readonly action: string,
        private readonly sinks: ReturnType<typeof usePostHog>,
    ) {}

    on(subjectType: string, subjectId: string): this {
        this.payload.subjectType = subjectType;
        this.payload.subjectId = subjectId;

        return this;
    }

    context(context: Record<string, unknown>): this {
        this.payload.context = { ...this.payload.context, ...context };

        return this;
    }

    duration(ms: number): this {
        this.payload.durationMs = ms;

        return this;
    }

    error(code: string | undefined, message: string): this {
        this.errorCode = code;
        this.errorMessage = message;

        return this;
    }

    log(): void {
        const properties = {
            ...this.payload.context,
            subject_type: this.payload.subjectType,
            subject_id: this.payload.subjectId,
            duration_ms: this.payload.durationMs,
        };

        if (this.errorMessage !== undefined) {
            this.sinks.trackError(this.action, this.errorMessage, {
                ...properties,
                error_code: this.errorCode,
            });
        } else {
            this.sinks.captureWithTenant(this.action, properties);
        }

        if (import.meta.env.DEV) {
            const debugProperties =
                this.errorMessage !== undefined ? { ...properties, error_message: this.errorMessage, error_code: this.errorCode } : properties;

            console.debug('[activity]', this.action, debugProperties);
        }
    }
}

export const useActivity = () => {
    const sinks = usePostHog();

    const activity = (action: string) => new ActivityBuilder(action, sinks);

    return { activity };
};
